import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import DataTable from 'components/Tables/DataTable';
import { axiosClient, getToken } from 'Config/axiosConfig';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { RootStore } from 'store/Store';
import { Dispatch } from 'redux';
import { LOADING_END, LOADING_OK, LoadingDispatchTypes, START_LOADING } from 'actions/Loading/loadingTypes';
import CircularProgress from '@mui/material/CircularProgress';

type Direccion = 'ingresos' | 'egresos';

function SincronizacionSAT(): JSX.Element {
  const dispatch = useDispatch();
  const { loading } = useSelector((state: RootStore) => state.loading);

  const nowIso = new Date().toISOString().slice(0, 16);
  const [fechaInicial, setFechaInicial] = useState<string>(nowIso);
  const [fechaFinal, setFechaFinal] = useState<string>(nowIso);
  // Por ahora solo se permiten solicitudes de ingresos
  const [direccion] = useState<Direccion>('ingresos');
  const [solicitudes, setSolicitudes] = useState<any[]>([]);
  const [verificandoId, setVerificandoId] = useState<string | null>(null);

  const toSatIso = (val: string) => (val.includes('T') ? `${val}:59` : val);

  const fetchSolicitudes = () => {
    return async (dispatch: Dispatch<LoadingDispatchTypes>) => {
      try {
        await getToken();
        dispatch({ type: START_LOADING });
        const resp = await axiosClient.get('/solicitud');
        setSolicitudes(resp.data || []);
        dispatch({ type: LOADING_END });
      } catch (error: any) {
        dispatch({ type: LOADING_END });
        const msg = error?.response?.data?.mensaje || error?.message || 'Error al obtener solicitudes';
        toast.error(msg);
      }
    };
  };

  useEffect(() => {
    dispatch<any>(fetchSolicitudes());
  }, []);

  const crearSolicitud = async () => {
    try {
      await getToken();
      dispatch({ type: START_LOADING });
      const payload = {
        FechaInicial: `${toSatIso(fechaInicial)}`,
        FechaFinal: `${toSatIso(fechaFinal)}`,
        direccion,
      };
      const resp = await axiosClient.post('/solicitudDescargaCFDI', payload);
      const data = resp.data || {};
      toast.success(data?.Mensaje || 'Solicitud registrada');
      dispatch<any>(fetchSolicitudes());
      dispatch({ type: LOADING_OK });
    } catch (error: any) {
      dispatch({ type: LOADING_END });
      const data = error?.response?.data || {};
      const msg = data?.mensaje || data?.msg || error?.message || 'Error al crear solicitud';
      toast.error(msg);
    }
  };

  const verificar = async (id_solicitud: string) => {
    try {
      setVerificandoId(id_solicitud);
      await getToken();
      dispatch({ type: START_LOADING });
      const resp = await axiosClient.post('/verificaSolicitudCFDI', { id_solicitud });
      const data = resp.data || {};
      if (Object.keys(data).length) {
        toast.info('Verificación enviada');
      } else {
        toast.info('Verificación procesada');
      }
      dispatch<any>(fetchSolicitudes());
      dispatch({ type: LOADING_OK });
    } catch (error: any) {
      dispatch({ type: LOADING_END });
      const data = error?.response?.data || {};
      const msg = data?.mensaje || data?.msg || error?.message || 'Error al verificar solicitud';
      toast.error(msg);
    } finally {
      setVerificandoId(null);
    }
  };

  const columns = useMemo(
    () => [
      { Header: 'Id Solicitud', accessor: 'id_solicitud' },
      { Header: 'Estado', accessor: 'estadoSolicitud' },
      { Header: 'Mensaje', accessor: 'mensaje' },
      { Header: 'CFDIs', accessor: 'numeroCFDIs' },
      { Header: 'Inicio', accessor: 'fecha_inicio' },
      { Header: 'Fin', accessor: 'fecha_fin' },
      // Por ahora solo manejamos ingresos; ocultar la columna de dirección para evitar confusión
      // { Header: 'Dirección', accessor: 'direccion' },
      { Header: 'Tipo', accessor: 'tipo_comprobante' },
      { Header: 'Acciones', accessor: 'acciones' },
    ],
    []
  );

  const rows = useMemo(
    () =>
      (solicitudes || []).map((s: any) => ({
        id_solicitud: s.id_solicitud,
        estadoSolicitud: s.estadoSolicitud,
        mensaje: s.mensaje,
        numeroCFDIs: s.numeroCFDIs,
        fecha_inicio: s.fecha_inicio,
        fecha_fin: s.fecha_fin,
        // direccion: s.direccion ? (s.direccion === 'egresos' ? 'Egresos' : 'Ingresos') : '',
        tipo_comprobante: s.tipo_comprobante,
        acciones: (
          <MDButton
            size='small'
            color='primary'
            onClick={() => verificar(s.id_solicitud)}
            disabled={verificandoId === s.id_solicitud}
            sx={{
              minWidth: 180,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {verificandoId === s.id_solicitud ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              <span>Verificar/Descargar</span>
            )}
          </MDButton>
        ),
      })),
    [solicitudes, verificandoId]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox p={3} lineHeight={1}>
            <MDTypography variant='h5' fontWeight='medium' color='primary'>
              Sincronización SAT
            </MDTypography>
          </MDBox>
          <MDBox px={3} pb={2}>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12} md={3}>
                <MDTypography variant='button' color='text'>Fecha Inicial</MDTypography>
                <input type='datetime-local' value={fechaInicial} onChange={(e) => setFechaInicial(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc' }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography variant='button' color='text'>Fecha Final</MDTypography>
                <input type='datetime-local' value={fechaFinal} onChange={(e) => setFechaFinal(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc' }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography variant='button' color='text'>Dirección</MDTypography>
                <input
                  type='text'
                  value='Ingresos'
                  readOnly
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc', backgroundColor: '#f5f5f5' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDButton onClick={crearSolicitud} color='primary' fullWidth>
                  Crear solicitud
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox p={3}>
            <DataTable table={{ columns, rows }} canSearch />
          </MDBox>
        </Card>
      </MDBox>

    </DashboardLayout>
  );
}

export default SincronizacionSAT;
